/**
 * Server-side search for the shared EventPicker.
 *
 * Replaces the fixed-window event dropdowns (limit 100/200/500, filtered in
 * the browser) that silently hid every event outside the window.
 *
 * Every event is reachable:
 *  - each group (Upcoming / Past) is paged with a keyset cursor on
 *    (start_time, id), so "Load more" walks the whole group — including runs of
 *    recurring events that share a title, venue and city (external audit F1);
 *  - the term also matches a calendar day ("2026-11-14", "11/14", "Nov 14") and
 *    an event id or id prefix (6+ hex digits).
 * Each request reads at most pageSize + 1 rows, far below PostgREST max_rows.
 */
import { supabase } from "./supabase";
import { sanitizePostgrestSearchTerm } from "../utils/postgrestSearch";
import {
  EVENT_PICKER_COLUMNS,
  EVENT_PICKER_PAGE_SIZE,
  parseEventDateQuery,
  parseEventIdPrefix,
  upcomingCutoff,
  type EventPickerOption,
} from "../utils/eventPicker";

export type EventPickerGroup = "upcoming" | "past";

/** Position after the last row of a page: its start_time and id, as returned by the server. */
export interface EventPickerCursor {
  start_time: string;
  id: string;
}

export interface EventPickerPage {
  rows: EventPickerOption[];
  /** null when this was the last page of the group. */
  nextCursor: EventPickerCursor | null;
}

const MAX_PAGE_SIZE = 100;

/** Double-quote a filter value for PostgREST's logic-tree grammar. */
const q = (v: string) => `"${v.replace(/["\\]/g, "")}"`;

/**
 * The search clauses ORed together for a term: text (title / venue / city),
 * each matching calendar day, and the id-prefix range. Returns null when the
 * term adds no filter (empty, or nothing searchable left after sanitizing).
 * Only the text clause carries user text, and only after sanitizing; date and
 * id clauses carry values this code generated.
 */
export function eventPickerSearchClauses(
  term: string,
  now: Date = new Date(),
): string | null {
  const clauses: string[] = [];
  const t = sanitizePostgrestSearchTerm(term);
  if (t) {
    clauses.push(
      `title.ilike.*${t}*`,
      `location_name.ilike.*${t}*`,
      `location_city.ilike.*${t}*`,
    );
  }
  for (const day of parseEventDateQuery(term, now)) {
    clauses.push(
      `and(start_time.gte.${q(day.start.toISOString())},start_time.lt.${q(day.end.toISOString())})`,
    );
  }
  const id = parseEventIdPrefix(term);
  if (id) {
    clauses.push(`and(id.gte.${id.lo},id.lte.${id.hi})`);
  }
  return clauses.length ? clauses.join(",") : null;
}

/**
 * One page of one group. Upcoming = start_time from local midnight today,
 * ascending; Past = before it, most recent first. Pass the previous page's
 * nextCursor to continue. Soft-deleted events are excluded.
 */
export async function searchEventPickerGroup(
  term: string,
  group: EventPickerGroup,
  options: {
    cursor?: EventPickerCursor | null;
    pageSize?: number;
    now?: Date;
  } = {},
): Promise<EventPickerPage> {
  const pageSize = Math.min(
    MAX_PAGE_SIZE,
    Math.max(1, Math.floor(options.pageSize ?? EVENT_PICKER_PAGE_SIZE)),
  );
  const now = options.now ?? new Date();
  const cutoff = upcomingCutoff(now).toISOString();
  const asc = group === "upcoming";

  let query = supabase
    .from("events")
    .select(EVENT_PICKER_COLUMNS)
    // is_deleted is nullable: NULL and false are both live events.
    .not("is_deleted", "is", true);
  query = asc
    ? query.gte("start_time", cutoff)
    : query.lt("start_time", cutoff);

  const search = eventPickerSearchClauses(term, now);
  if (search) {
    query = query.or(search);
  }

  const c = options.cursor;
  if (c) {
    // Keyset: strictly after the cursor in this group's (start_time, id) order.
    const op = asc ? "gt" : "lt";
    query = query.or(
      `start_time.${op}.${q(c.start_time)},and(start_time.eq.${q(c.start_time)},id.${op}.${q(c.id)})`,
    );
  }

  const { data, error } = await query
    .order("start_time", { ascending: asc })
    .order("id", { ascending: asc })
    .limit(pageSize + 1);
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as EventPickerOption[];
  const page = rows.slice(0, pageSize);
  const last = page[page.length - 1];
  return {
    rows: page,
    nextCursor:
      rows.length > pageSize && last?.start_time
        ? { start_time: last.start_time, id: last.id }
        : null,
  };
}

/**
 * Load one event by id so an edit form can show its saved selection even when
 * that event is not in the current search results. Deliberately NOT filtered
 * on is_deleted: a record linked to a since-deleted event must still say so
 * rather than look unlinked. Returns null when the id matches no visible row.
 */
export async function getEventPickerOptionById(
  id: string,
): Promise<EventPickerOption | null> {
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_PICKER_COLUMNS)
    .eq("id", id)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data as EventPickerOption | null) ?? null;
}
